import type { DateKey, HHMM } from './time';
import { addDays } from './time';

export type Scene = 'morning' | 'evening' | 'outdoor' | 'focus' | 'dashboard' | 'family';

export interface SceneDef {
  key: Scene;
  title: string;
  desc: string;
  shows: string;
  icon: 'sun' | 'moon' | 'leaf' | 'grid' | 'dash' | 'home';
}

export const SCENES: SceneDef[] = [
  { key: 'morning', title: 'A gentle morning', desc: 'Weather and today’s plans, front and center.', shows: 'WEATHER + FAMILY AGENDA', icon: 'sun' },
  { key: 'evening', title: 'Time to slow down', desc: 'A quieter, dimmer display for the end of the day.', shows: 'EVENING OVERVIEW', icon: 'moon' },
  { key: 'outdoor', title: 'A little greener', desc: 'Bring outdoor conditions and air quality into focus.', shows: 'OUTDOOR READINGS', icon: 'leaf' },
  { key: 'focus', title: 'Room to focus', desc: 'Just the clock and your next plan. Nothing else.', shows: 'MINIMAL DISPLAY', icon: 'grid' },
  { key: 'dashboard', title: 'Family dashboard', desc: 'Time, chores, salat times, weather and the day at a glance.', shows: 'EVERYTHING AT ONCE', icon: 'dash' },
  { key: 'family', title: 'Our family', desc: 'The dashboard on warm paper, with a background you choose.', shows: 'WARM DASHBOARD', icon: 'home' },
];

export interface ScenePrefs {
  scene: Scene;
  follow_schedule: boolean;
  morning_starts: HHMM;
  evening_starts: HHMM;
}

/** Which scheduled scene covers a wall-clock time. */
export function scheduledScene(now: HHMM, morningStarts: HHMM, eveningStarts: HHMM): 'morning' | 'evening' {
  if (morningStarts <= eveningStarts) return now >= morningStarts && now < eveningStarts ? 'morning' : 'evening';
  return now >= eveningStarts && now < morningStarts ? 'evening' : 'morning';
}

/** The most recent schedule boundary at or before `now`, as "YYYY-MM-DD HH:MM" wall-clock. */
export function lastBoundary(nowDate: DateKey, now: HHMM, morningStarts: HHMM, eveningStarts: HHMM): string {
  const candidates = [
    `${nowDate} ${morningStarts}`,
    `${nowDate} ${eveningStarts}`,
    `${addDays(nowDate, -1)} ${morningStarts}`,
    `${addDays(nowDate, -1)} ${eveningStarts}`,
  ];
  const cur = `${nowDate} ${now}`;
  return candidates.filter((c) => c <= cur).sort().pop()!;
}

/**
 * The scene to show. A manual choice (stored with its local wall-clock time `chosenAt`)
 * holds until the next schedule boundary when following the schedule.
 */
export function effectiveScene(
  prefs: ScenePrefs,
  now: { dateKey: DateKey; hhmm: HHMM },
  chosenAt: { dateKey: DateKey; hhmm: HHMM } | null,
): Scene {
  if (!prefs.follow_schedule) return prefs.scene;
  const boundary = lastBoundary(now.dateKey, now.hhmm, prefs.morning_starts, prefs.evening_starts);
  if (chosenAt && `${chosenAt.dateKey} ${chosenAt.hhmm}` >= boundary) return prefs.scene;
  return scheduledScene(now.hhmm, prefs.morning_starts, prefs.evening_starts);
}

/** Evening dim overlay opacity: on after 22:00 and before morning starts. */
export function dimOpacity(scene: Scene, now: HHMM, morningStarts: HHMM): number {
  if (scene !== 'evening') return 0;
  return now >= '22:00' || now < morningStarts ? 0.4 : 0;
}
